/**
 * LandingNav — sticky top navigation for the public landing page.
 *
 * Uses the shared DysonSphere mini variant as the brand mark so any
 * visual tweak propagates everywhere (sidebar, splash, landing).
 *
 * Mobile (≤ 860px): the inline links + Sign-in collapse, a hamburger
 * appears, and tapping it slides down a panel with the same links +
 * Sign-in stacked. Esc, outside-click, or any link selection closes
 * the panel — same affordances the user expects from every other
 * mobile site.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { DysonSphere } from "@/components/ui/DysonSphere";
import { Icon } from "./LandingIcons";

export interface LandingNavProps {
  onThemeToggle?: () => void;
  theme?: "dark" | "light";
}

export function LandingNav({ onThemeToggle, theme = "dark" }: LandingNavProps) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const h = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  // Close on Escape — same affordance as native dialogs / shells.
  React.useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // When the panel is open the body shouldn't scroll behind it —
  // matches iOS Safari / Chrome behaviour for modal nav overlays.
  React.useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const links = [
    { label: "Platform", href: "#platform" },
    { label: "Developers", href: "#developers" },
    { label: "Marketplace", href: "#marketplace" },
    { label: "Pricing", href: "#pricing" },
    { label: "Docs", href: "#docs" },
  ];

  const closeMenu = React.useCallback(() => setMenuOpen(false), []);

  return (
    <nav
      className={`nav ${scrolled ? "nav--scrolled" : ""}`}
      aria-label="primary"
    >
      <div className="container nav__inner">
        <a href="#top" className="nav__logo" onClick={closeMenu}>
          <DysonSphere size={22} variant="mini" />
          <span>OMNIFORCE</span>
        </a>
        <div className="nav__links">
          {links.map((l) => (
            <a key={l.href} href={l.href}>
              {l.label}
            </a>
          ))}
        </div>
        <div className="nav__cta">
          {onThemeToggle && (
            <button
              onClick={onThemeToggle}
              className="btn btn--sm"
              style={{
                background: "transparent",
                border: "1px solid var(--line)",
                padding: "0 10px",
                height: 34,
                color: "var(--fg-muted)",
              }}
              aria-label="Toggle theme"
            >
              <Icon name={theme === "dark" ? "sun" : "moon"} size={14} />
            </button>
          )}
          <button
            className="nav__signin"
            onClick={() => navigate("/sign-in")}
          >
            Sign in
          </button>
          <button
            className="btn btn--primary btn--sm"
            onClick={() => navigate("/sign-in")}
          >
            Get Omni <Icon name="arrow" size={14} />
          </button>
          {/* Hamburger — only rendered ≤ 860px via CSS. Stays inside
              ``.nav__cta`` so its alignment + spacing inherit the
              same row layout the rest of the actions use. */}
          <button
            className="nav__hamburger"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="landing-mobile-menu"
          >
            <Icon name={menuOpen ? "close" : "menu"} size={18} />
          </button>
        </div>
      </div>

      {/* Mobile dropdown panel. Always rendered so the slide-in
          transition has an anchor; visibility + interactivity gated
          on ``data-open``. The backdrop sits behind it and closes
          the menu on tap. */}
      <div
        className="nav__mobile-backdrop"
        data-open={menuOpen ? "true" : "false"}
        aria-hidden="true"
        onClick={closeMenu}
      />
      <div
        id="landing-mobile-menu"
        className="nav__mobile-panel"
        data-open={menuOpen ? "true" : "false"}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile menu"
      >
        <ul>
          {links.map((l) => (
            <li key={l.href}>
              <a href={l.href} onClick={closeMenu}>
                {l.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="nav__mobile-actions">
          <button
            className="btn btn--ghost"
            onClick={() => {
              closeMenu();
              navigate("/sign-in");
            }}
          >
            Sign in
          </button>
          <button
            className="btn btn--primary"
            onClick={() => {
              closeMenu();
              navigate("/sign-in");
            }}
          >
            Get Omni <Icon name="arrow" size={14} />
          </button>
        </div>
      </div>
    </nav>
  );
}
